// System pages: roles (what each role may do) and approval rules (who approves what, from what
// amount), and the audit trail.
import { useState } from "react";
import { Page, Card, Btn, Tabs, Table, Tr, Td, Field, Loading, Pill } from "@/components/kit";
import api from "@/lib/api";
import { act, input, k0, Num, n, Select, useLoad } from "@/components/erp";
import RolePlanner from "@/pages/RolePlanner";

export function Roles() {
  const [tab, setTab] = useState("roles");
  return (
    <Page title="Roles & approvals">
      <Tabs value={tab} onChange={setTab} tabs={[{ value: "roles", label: "Roles" }, { value: "plan", label: "Shrink roles" }, { value: "rules", label: "Approval rules" }]} />
      {tab === "roles" ? <RoleList /> : tab === "plan" ? <RolePlanner /> : <Rules />}
    </Page>
  );
}

function RoleList() {
  const { data: roles, reload } = useLoad("/auth/roles");
  const { data: perms } = useLoad("/auth/permissions");
  const [edit, setEdit] = useState(null);
  if (!roles || !perms) return <Loading />;
  const groups = [...new Set(perms.map((p) => p.group))];
  return (
    <div className="mt-4 grid gap-5 lg:grid-cols-[360px_1fr]">
      <Card className="h-fit bg-white">
        {["admin", "ops"].map((side) => (
          <div key={side} className="mb-4">
            <div className="text-xs font-semibold uppercase text-slate-400">{side === "admin" ? "Admin side" : "Operations side"}</div>
            {roles.filter((r) => r.side === side).map((r) => (
              <div key={r.key} className="flex items-center gap-1">
                <button type="button" onClick={() => setEdit({ ...r })}
                  className={`mt-1 flex w-full justify-between rounded-md px-3 py-2 text-left text-sm hover:bg-slate-50 ${edit?.key === r.key ? "bg-[#E7F0EA] font-semibold" : ""} ${r.active ? "" : "text-slate-400"}`}>
                  <span>{r.label}{!r.active && " (off)"}</span><span className="text-xs text-slate-500">{r.users} people</span>
                </button>
                {!r.active && <Btn kind="link" className="text-xs" onClick={async () => { if (await act(() => api.post(`/auth/roles/${r.key}/active`, null, { params: { on: true } }), "Switched on")) reload(); }}>Switch on</Btn>}
              </div>
            ))}
          </div>
        ))}
        <Btn onClick={() => setEdit({ key: "", label: "", side: "ops", about: "", perms: [], isNew: true })}>+ New role</Btn>
      </Card>
      {edit && (
        <Card className="bg-white">
          <div className="grid gap-3 sm:grid-cols-3">
            {edit.isNew && <Field label="Short code (e.g. yard_clerk)"><input className={input} value={edit.key} onChange={(e) => setEdit({ ...edit, key: e.target.value.toLowerCase() })} /></Field>}
            <Field label="Name"><input className={input} value={edit.label} onChange={(e) => setEdit({ ...edit, label: e.target.value })} /></Field>
            <Field label="Side"><Select value={edit.side} empty={null} onChange={(v) => setEdit({ ...edit, side: v })} options={[{ value: "ops", label: "Operations" }, { value: "admin", label: "Admin" }]} /></Field>
          </div>
          <Field label="What they do"><input className={`${input} mt-1`} value={edit.about} onChange={(e) => setEdit({ ...edit, about: e.target.value })} /></Field>
          {edit.all ? <p className="mt-4 text-sm">The Owner can do everything.</p> : groups.map((g) => (
            <div key={g} className="mt-4">
              <div className="text-sm font-semibold">{g}</div>
              <div className="mt-1 grid gap-1 sm:grid-cols-2">
                {perms.filter((p) => p.group === g).map((p) => (
                  <label key={p.key} className="flex items-start gap-2 text-sm">
                    <input type="checkbox" className="mt-1" checked={edit.perms.includes(p.key)}
                      onChange={(e) => setEdit({ ...edit, perms: e.target.checked ? [...edit.perms, p.key] : edit.perms.filter((x) => x !== p.key) })} />{p.label}
                  </label>
                ))}
              </div>
            </div>
          ))}
          <div className="mt-5 flex gap-2">
            <Btn kind="primary" onClick={async () => { if (await act(() => api.put(`/auth/roles/${edit.key}`, edit), "Role saved - its people sign in again")) { setEdit(null); reload(); } }}>Save role</Btn>
            <Btn onClick={() => setEdit(null)}>Close</Btn>
          </div>
        </Card>
      )}
    </div>
  );
}

function Rules() {
  const { data: rules, reload } = useLoad("/approvals/rules");
  const { data: perms } = useLoad("/auth/permissions");
  const [edit, setEdit] = useState(null);
  if (!rules || !perms) return <Loading />;
  const plabel = Object.fromEntries(perms.map((p) => [p.key, p.label]));
  return (
    <div className="mt-4 space-y-3">
      <p className="text-sm text-slate-600">Each step applies from its amount, in order. Whoever raised a request never approves it, and nobody approves two steps of one request.</p>
      {rules.map((r) => (
        <Card key={r.key} title={r.label} className="bg-white">
          {edit?.key === r.key ? (
            <>
              {edit.steps.map((s, i) => (
                <div key={i} className="mt-2 grid gap-2 sm:grid-cols-[1fr_160px_auto]">
                  <Select value={s.perm} empty={null} onChange={(v) => setEdit({ ...edit, steps: edit.steps.map((x, j) => (j === i ? { ...x, perm: v } : x)) })}
                    options={perms.filter((p) => /approve|final/.test(p.key)).map((p) => ({ value: p.key, label: p.label }))} />
                  <Num value={s.min_amount} onChange={(v) => setEdit({ ...edit, steps: edit.steps.map((x, j) => (j === i ? { ...x, min_amount: v } : x)) })} />
                  <Btn onClick={() => setEdit({ ...edit, steps: edit.steps.filter((_, j) => j !== i) })}>Remove</Btn>
                </div>
              ))}
              <div className="mt-3 flex gap-2">
                <Btn onClick={() => setEdit({ ...edit, steps: [...edit.steps, { perm: edit.steps[0]?.perm, min_amount: 0 }] })}>+ Step</Btn>
                <Btn kind="primary" onClick={async () => { if (await act(() => api.put(`/approvals/rules/${r.key}`, { steps: edit.steps.map((s) => ({ ...s, min_amount: n(s.min_amount) || 0 })) }), "Saved")) { setEdit(null); reload(); } }}>Save</Btn>
                <Btn onClick={() => setEdit(null)}>Cancel</Btn>
              </div>
            </>
          ) : (
            <>
              <ol className="mt-2 space-y-1 text-sm">{r.steps.map((s, i) => <li key={i}>{i + 1}. {plabel[s.perm] || s.perm} — from KES {k0(s.min_amount)}{s.only_above_allowance ? " (only above the allowance)" : ""}</li>)}</ol>
              <Btn kind="link" onClick={() => setEdit({ key: r.key, steps: r.steps.map((s) => ({ ...s })) })}>Change</Btn>
            </>
          )}
        </Card>
      ))}
    </div>
  );
}

export function Audit() {
  const [what, setWhat] = useState("");
  const { data } = useLoad("/audit", { what: what || undefined });
  return (
    <Page title="Audit trail">
      <p className="mt-2 text-sm text-slate-600">Every change: who, when, and what it was before and after.</p>
      <div className="mt-3 max-w-xs"><Select value={what} onChange={(v) => setWhat(v || "")} empty="Everything"
        options={["expense", "fuel_issue", "fuel_receipt", "fuel_dip", "quote", "payees", "trucks", "role", "approval_rule"].map((v) => ({ value: v, label: v.replace("_", " ") }))} /></div>
      {!data ? <Loading /> : (
        <Table minWidth={820} cols={[{ label: "When" }, { label: "Who" }, { label: "What" }, { label: "Action" }, { label: "Changes" }]}>
          {data.map((a) => (
            <Tr key={a._id}>
              <Td className="whitespace-nowrap">{a.at?.slice(0, 16).replace("T", " ")}</Td><Td>{a.by_name || a.by}</Td><Td>{a.what}</Td><Td><Pill>{a.action || "changed"}</Pill></Td>
              <Td className="text-xs text-slate-600">{a.changes ? Object.entries(a.changes).slice(0, 6).map(([k, v]) => <div key={k}><b>{k}</b>: {String(v.from ?? "—")} → {String(v.to ?? "—")}</div>) : ""}</Td>
            </Tr>
          ))}
        </Table>
      )}
    </Page>
  );
}
